import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Copy, Download, FileSpreadsheet, Lock, Plus, ReceiptIndianRupee, RefreshCw, RotateCcw, Trash2, Unlock } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { StatusPill } from "@/components/CommandSystem";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { accountApi, blankTemplate, downloadBlob, monthStart, today } from "./accounts/accountApi";
import { Card, DataTable, Field, MetricStrip, MetricTile, SectionTitle, TabBar } from "./accounts/AccountPrimitives";
import { GSTSummaryTab, InvoicesTab, PayablesTab, PaymentsTab } from "./accounts/AccountRecordTabs";
import { AccountWorkbench } from "./accounts/AccountWorkbench";
import { useAccountMutation } from "./accounts/useAccountMutation";

const tabs = [
  ["workbench", "Workbench"],
  ["invoices", "Receivables"],
  ["payables", "Payables"],
  ["gst", "GST"],
  ["export", "Export"],
  ["controls", "Controls"],
  ["advanced", "Advanced"],
];

const advancedTabs = [
  ["payments", "Payments"],
  ["templates", "Templates"],
  ["ledgers", "Ledgers"],
  ["trust", "Trust checks"],
];

const fallbackExportTypes = ["sales", "receipts", "payments", "payables", "gst_summary"];
const fallbackMappedFields = {
  sales: ["invoice_number", "invoice_date", "client_name", "client_gst_number", "campaign_name", "branch_name", "taxable_amount", "gst_mode", "gst_percentage", "cgst_amount", "sgst_amount", "igst_amount", "total_gst_amount", "final_amount", "received_amount", "pending_amount", "due_date", "payment_status", "narration", "revenue_ledger_name", "cgst_ledger_name", "sgst_ledger_name", "igst_ledger_name", "party_ledger_name"],
  receipts: ["receipt_date", "client_name", "invoice_number", "campaign_name", "amount_received", "payment_mode", "reference_number", "narration", "party_ledger_name"],
  payments: ["payment_date", "payable_type", "vendor_name", "landowner_name", "hoarding_name", "amount", "gst_amount", "total_amount", "payment_mode", "narration", "expense_ledger_name", "party_ledger_name"],
  payables: ["payment_date", "payable_type", "vendor_name", "landowner_name", "hoarding_name", "amount", "gst_amount", "total_amount", "payment_mode", "narration", "expense_ledger_name", "party_ledger_name"],
  gst_summary: ["period_from", "period_to", "taxable_sales", "output_cgst", "output_sgst", "output_igst", "input_cgst", "input_sgst", "input_igst", "net_gst_payable"],
};

const fallbackLedgerDefaults = [
  ["advertising_revenue", "Advertising Revenue"],
  ["output_cgst", "Output CGST"],
  ["output_sgst", "Output SGST"],
  ["output_igst", "Output IGST"],
  ["landowner_rent", "Landowner Rent"],
  ["printing_vendor", "Printing Vendor"],
  ["mounting_vendor", "Mounting Vendor"],
  ["electricity", "Electricity"],
  ["maintenance", "Maintenance"],
  ["other", "Other"],
];

const payableTypes = ["landowner_rent", "printing_vendor", "mounting_vendor", "electricity", "maintenance", "other"];
const accentBar = { blue: "bg-blue-500", emerald: "bg-emerald-500", amber: "bg-amber-500", rose: "bg-rose-500", slate: "bg-slate-400" };

function bucketTotal(report, key) {
  return (report?.buckets || []).find((b) => b.bucket === key)?.amount || 0;
}

function paymentStatusTone(status) {
  const key = String(status || "").toLowerCase();
  if (key === "paid") return "emerald";
  if (key === "overdue") return "rose";
  if (key === "pending" || key === "partial") return "amber";
  return "slate";
}

/* ------------------------------- Page ------------------------------- */

export function Accounts() {
  const [tab, setTab] = useState("workbench");
  const [advancedTab, setAdvancedTab] = useState("payments");
  const [filters, setFilters] = useState({ from: monthStart, to: today });
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountSummary, setAccountSummary] = useState(null);
  const [accountSchema, setAccountSchema] = useState(null);
  const [invoiceCandidates, setInvoiceCandidates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [payables, setPayables] = useState([]);
  const [summary, setSummary] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [ledgerMappings, setLedgerMappings] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [reconciliation, setReconciliation] = useState([]);
  const [reconciliationMeta, setReconciliationMeta] = useState({ total: 0, hasMore: false });
  const [trialBalance, setTrialBalance] = useState(null);
  const [receivableAging, setReceivableAging] = useState(null);
  const [payableAging, setPayableAging] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ invoice_date: today, client_name: "", base_amount: "", gst: { gst_mode: "exclusive", gst_percentage: 18, gst_treatment: "cgst_sgst" }, notes: "" });
  const [paymentForm, setPaymentForm] = useState({ invoice_id: "", amount: "", payment_date: today, payment_mode: "bank", payment_type: "installment", reference_number: "" });
  const [payableForm, setPayableForm] = useState({ payable_type: "landowner_rent", vendor_name: "", landowner_name: "", amount: "", gst_applicable: false, gst_mode: "exclusive", gst_percentage: 18, due_date: today });
  const [payablePaymentForm, setPayablePaymentForm] = useState({ payable_id: "", amount: "", payment_date: today, payment_mode: "bank", reference_number: "" });
  const [adjustmentForm, setAdjustmentForm] = useState({ adjustment_type: "credit_note", source_type: "invoice", source_id: "", entry_date: today, party_name: "", amount: "", gst_amount: "", reason: "" });
  const [templateForm, setTemplateForm] = useState(blankTemplate());
  const [editingTemplate, setEditingTemplate] = useState("");
  const [exportForm, setExportForm] = useState({ export_type: "sales", from: monthStart, to: today, template_id: "", require_locked_period: true, require_clean_reconcile: true, require_ledger_mappings: true });
  const [preview, setPreview] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [payableDialogOpen, setPayableDialogOpen] = useState(false);
  const [settlePayableDialogOpen, setSettlePayableDialogOpen] = useState(false);
  const mutateAccount = useAccountMutation({ reload: () => loadAll(tab) });

  const exportTypes = useMemo(() => (accountSchema?.export_types || []).map((type) => type.key), [accountSchema]);
  const mappedFields = useMemo(() => {
    if (!accountSchema?.export_types) return fallbackMappedFields;
    return accountSchema.export_types.reduce((acc, type) => {
      acc[type.key] = (type.fields || []).map((field) => field.key);
      return acc;
    }, {});
  }, [accountSchema]);
  const ledgerDefaults = useMemo(() => {
    if (!accountSchema?.ledger_defaults?.length) return fallbackLedgerDefaults;
    return accountSchema.ledger_defaults.map((item) => [item.category_key, item.ledger_name]);
  }, [accountSchema]);
  const availableExportTypes = exportTypes.length ? exportTypes : fallbackExportTypes;

  async function loadAll(targetTab = tab) {
    setAccountsLoading(true);
    const applyReconciliation = (data) => {
      const issues = Array.isArray(data) ? data : (data?.issues || []);
      setReconciliation(issues);
      setReconciliationMeta({ total: Array.isArray(data) ? issues.length : (data?.total_issues || 0), hasMore: Boolean(data?.has_more) });
    };
    const requests = [
      { label: "summary", run: () => accountApi.summary(filters), apply: (data) => setAccountSummary(data || null) },
    ];
    if (!accountSchema) {
      requests.push({ label: "export schema", run: accountApi.schema, apply: (data) => setAccountSchema(data || null) });
    }
    if (targetTab === "workbench") {
      requests.push({ label: "campaign invoice suggestions", run: accountApi.campaignInvoiceCandidates, apply: (data) => setInvoiceCandidates(data || []) });
    }
    if (targetTab === "invoices") {
      requests.push({ label: "invoices", run: () => accountApi.invoices(filters), apply: (data) => setInvoices(data || []) });
    }
    if (targetTab === "payments") {
      requests.push(
        { label: "invoices", run: () => accountApi.invoices(filters), apply: (data) => setInvoices(data || []) },
        { label: "payments", run: () => accountApi.payments(filters), apply: (data) => setPayments(data || []) },
      );
    }
    if (targetTab === "payables") {
      requests.push({ label: "payables", run: () => accountApi.payables(filters), apply: (data) => setPayables(data || []) });
    }
    if (targetTab === "gst") {
      requests.push({ label: "GST summary", run: () => accountApi.gstSummary(filters), apply: (data) => setSummary(data || null) });
    }
    if (targetTab === "advanced") {
      requests.push(
        { label: "payments", run: () => accountApi.payments(filters), apply: (data) => setPayments(data || []) },
        { label: "export templates", run: accountApi.templates, apply: (data) => setTemplates(data || []) },
        { label: "ledger mappings", run: accountApi.ledgers, apply: (data) => setLedgerMappings(data || []) },
        { label: "reconciliation", run: accountApi.reconciliation, apply: applyReconciliation },
        { label: "trial balance", run: () => accountApi.trialBalance(filters), apply: (data) => setTrialBalance(data || null) },
        { label: "receivable aging", run: () => accountApi.receivableAging(filters), apply: (data) => setReceivableAging(data || null) },
        { label: "payable aging", run: () => accountApi.payableAging(filters), apply: (data) => setPayableAging(data || null) },
      );
    }
    if (targetTab === "templates") {
      requests.push({ label: "export templates", run: accountApi.templates, apply: (data) => setTemplates(data || []) });
    }
    if (targetTab === "export") {
      requests.push(
        { label: "export templates", run: accountApi.templates, apply: (data) => setTemplates(data || []) },
        { label: "ledger mappings", run: accountApi.ledgers, apply: (data) => setLedgerMappings(data || []) },
      );
    }
    if (targetTab === "ledgers") {
      requests.push({ label: "ledger mappings", run: accountApi.ledgers, apply: (data) => setLedgerMappings(data || []) });
    }
    if (targetTab === "trust") {
      requests.push(
        { label: "reconciliation", run: accountApi.reconciliation, apply: applyReconciliation },
        { label: "trial balance", run: () => accountApi.trialBalance(filters), apply: (data) => setTrialBalance(data || null) },
        { label: "receivable aging", run: () => accountApi.receivableAging(filters), apply: (data) => setReceivableAging(data || null) },
        { label: "payable aging", run: () => accountApi.payableAging(filters), apply: (data) => setPayableAging(data || null) },
      );
    }
    if (targetTab === "controls") {
      requests.push(
        { label: "invoices", run: () => accountApi.invoices(filters), apply: (data) => setInvoices(data || []) },
        { label: "accounting periods", run: accountApi.periods, apply: (data) => setPeriods(data || []) },
        { label: "reconciliation", run: accountApi.reconciliation, apply: applyReconciliation },
      );
    }
    try {
      const results = await Promise.allSettled(requests.map((request) => request.run()));
      const failed = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          requests[index].apply(result.value.data);
        } else {
          failed.push(requests[index].label);
        }
      });
      if (failed.length > 0) {
        const firstError = results.find((result) => result.status === "rejected")?.reason;
        toast.error(`${failed.join(", ")} failed to load: ${formatApiError(firstError?.response?.data?.detail) || firstError?.message || "Request failed"}`);
      }
    } finally {
      setAccountsLoading(false);
    }
  }

  useEffect(() => { loadAll(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const templatesForExport = useMemo(() => templates.filter((t) => t.export_type === exportForm.export_type), [templates, exportForm.export_type]);

  async function createInvoice(e) {
    e.preventDefault();
    await mutateAccount(
      () => api.post("/accounts/invoices", { ...invoiceForm, base_amount: Number(invoiceForm.base_amount || 0) }),
      { success: "Invoice created", reset: () => setInvoiceForm({ ...invoiceForm, client_name: "", base_amount: "", notes: "" }), close: setInvoiceDialogOpen },
    );
  }

  async function generateCampaignInvoice(campaignId) {
    await mutateAccount(
      () => accountApi.createCampaignInvoice(campaignId),
      {
        success: "Invoice generated from campaign",
        reset: async () => {
          const { data } = await accountApi.campaignInvoiceCandidates();
          setInvoiceCandidates(data || []);
        },
      },
    );
  }

  async function createPayment(e) {
    e.preventDefault();
    await mutateAccount(
      () => api.post("/accounts/payments", { ...paymentForm, invoice_id: paymentForm.invoice_id || null, amount: Number(paymentForm.amount || 0) }),
      { success: "Payment recorded", reset: () => setPaymentForm({ ...paymentForm, amount: "", reference_number: "" }), close: setPaymentDialogOpen },
    );
  }

  async function createPayable(e) {
    e.preventDefault();
    await mutateAccount(
      () => api.post("/accounts/payables", { ...payableForm, amount: Number(payableForm.amount || 0), gst_percentage: Number(payableForm.gst_percentage || 0) }),
      { success: "Payable recorded", reset: () => setPayableForm({ ...payableForm, vendor_name: "", landowner_name: "", amount: "" }), close: setPayableDialogOpen },
    );
  }

  async function createPayablePayment(e) {
    e.preventDefault();
    if (!payablePaymentForm.payable_id) {
      toast.error("Select a payable to settle.");
      return;
    }
    await mutateAccount(
      () => api.post(`/accounts/payables/${payablePaymentForm.payable_id}/payments`, { ...payablePaymentForm, amount: Number(payablePaymentForm.amount || 0) }),
      { success: "Settlement recorded", reset: () => setPayablePaymentForm({ ...payablePaymentForm, amount: "", reference_number: "" }), close: setSettlePayableDialogOpen },
    );
  }

  async function reversePayment(id) {
    await mutateAccount(() => api.post(`/accounts/payments/${id}/reverse`, { reversal_date: today, reason: "Reversed from Accounts" }), { success: "Payment reversed" });
  }

  async function createAdjustment(e) {
    e.preventDefault();
    await mutateAccount(
      () => api.post("/accounts/adjustments", { ...adjustmentForm, amount: Number(adjustmentForm.amount || 0), gst_amount: Number(adjustmentForm.gst_amount || 0) }),
      { success: "Adjustment posted", reset: () => setAdjustmentForm({ ...adjustmentForm, source_id: "", party_name: "", amount: "", gst_amount: "", reason: "" }) },
    );
  }

  function setColumn(index, patch) {
    setTemplateForm((prev) => ({
      ...prev,
      columns: prev.columns.map((col, i) => i === index ? { ...col, ...patch } : col),
    }));
  }

  async function saveTemplate(e) {
    e.preventDefault();
    try {
      const payload = { ...templateForm, columns: templateForm.columns.map((col, i) => ({ ...col, column_order: i + 1 })) };
      const { data } = editingTemplate
        ? await api.put(`/accounts/export-templates/${editingTemplate}`, payload)
        : await api.post("/accounts/export-templates", payload);
      toast.success(editingTemplate ? "Template updated" : "Template created");
      setEditingTemplate("");
      setTemplateForm(data || blankTemplate());
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  async function duplicateTemplate(id) {
    await api.post(`/accounts/export-templates/${id}/duplicate`);
    toast.success("Template duplicated");
    loadAll();
  }

  async function deleteTemplate(id) {
    await api.delete(`/accounts/export-templates/${id}`);
    toast.success("Template disabled");
    loadAll();
  }

  async function saveLedger(key, value) {
    await api.post("/accounts/ledger-mappings", { category_key: key, ledger_name: value });
    toast.success("Ledger mapping saved");
    loadAll();
  }

  async function previewExport() {
    if (!exportForm.template_id) {
      toast.error("Select an export template first.");
      return;
    }
    const { data } = await api.post("/accounts/tally-export/preview", exportForm);
    setPreview(data);
  }

  async function downloadExport() {
    const response = await api.post("/accounts/tally-export/download", exportForm, { responseType: "blob" });
    downloadBlob(response, "hoardexa-export.xlsx");
    toast.success("Export downloaded");
  }

  async function setCurrentPeriodStatus(status) {
    const reason = status === "locked" ? "Month closed after finance review" : "Reopened for correction";
    await api.post("/accounts/periods/status", { period_start: filters.from, period_end: filters.to, status, reason });
    toast.success(status === "locked" ? "Period locked" : "Period reopened");
    loadAll();
  }

  /* ------------------------------ Columns ------------------------------ */

  const invoiceColumns = [
    { key: "invoice_number", header: "Invoice", cellClassName: "font-semibold text-slate-900" },
    { key: "client_name", header: "Client" },
    { key: "taxable_amount", header: "Taxable", align: "right", render: (r) => inr(r.taxable_amount) },
    { key: "total_gst_amount", header: "GST", align: "right", render: (r) => inr(r.total_gst_amount) },
    { key: "final_amount", header: "Final", align: "right", render: (r) => inr(r.final_amount) },
    { key: "received_amount", header: "Received", align: "right", render: (r) => inr(r.received_amount) },
    { key: "payment_status", header: "Status", render: (r) => <StatusPill tone={paymentStatusTone(r.payment_status)}>{r.payment_status}</StatusPill> },
    { key: "due_date", header: "Due", align: "right", render: (r) => fmtDate(r.due_date) },
  ];

  const paymentColumns = [
    { key: "payment_date", header: "Date", render: (r) => fmtDate(r.payment_date) },
    { key: "amount", header: "Amount", align: "right", render: (r) => inr(r.amount) },
    { key: "payment_mode", header: "Mode", render: (r) => <span className="capitalize">{r.payment_mode}</span> },
    { key: "reference_number", header: "Reference", render: (r) => r.reference_number || "—" },
    { key: "payment_type", header: "Type", render: (r) => <span className="capitalize">{r.payment_type}</span> },
    { key: "notes", header: "Notes", render: (r) => r.notes || "—" },
    { key: "action", header: "Action", align: "right", render: (r) => (
      <Button size="sm" variant="outline" onClick={() => reversePayment(r.id)}><RotateCcw size={14} /> Reverse</Button>
    ) },
  ];

  const payableColumns = [
    { key: "payable_type", header: "Type", cellClassName: "capitalize font-medium text-slate-900", render: (r) => r.payable_type?.replaceAll("_", " ") },
    { key: "vendor", header: "Vendor", render: (r) => r.vendor_name || r.landowner_name || "—" },
    { key: "taxable_amount", header: "Taxable", align: "right", render: (r) => inr(r.taxable_amount) },
    { key: "gst_amount", header: "GST", align: "right", render: (r) => inr(r.gst_amount) },
    { key: "total_amount", header: "Total", align: "right", render: (r) => inr(r.total_amount) },
    { key: "paid_amount", header: "Paid", align: "right", render: (r) => inr(r.paid_amount) },
    { key: "status", header: "Status", render: (r) => <StatusPill tone={paymentStatusTone(r.status)}>{r.status}</StatusPill> },
    { key: "due_date", header: "Due", align: "right", render: (r) => fmtDate(r.due_date) },
  ];

  const templateColumns = [
    { key: "template_name", header: "Template", cellClassName: "font-semibold text-slate-900" },
    { key: "export_type", header: "Type" },
    { key: "file_type", header: "File", render: (r) => <span className="uppercase">{r.file_type}</span> },
    { key: "columns", header: "Columns", align: "right", render: (r) => r.columns?.length || 0 },
    { key: "actions", header: "Actions", align: "right", render: (r) => (
      <div className="flex justify-end gap-1.5">
        <Button size="sm" variant="outline" aria-label="Edit template" onClick={() => { setEditingTemplate(r.id); setTemplateForm(r); }}><FileSpreadsheet size={14} /></Button>
        <Button size="sm" variant="outline" aria-label="Duplicate template" onClick={() => duplicateTemplate(r.id)}><Copy size={14} /></Button>
        <Button size="sm" variant="outline" aria-label="Disable template" onClick={() => deleteTemplate(r.id)}><Trash2 size={14} /></Button>
      </div>
    ) },
  ];

  const trialColumns = [
    { key: "ledger_name", header: "Ledger", cellClassName: "font-semibold text-slate-900" },
    { key: "branch_type", header: "Branch", render: (r) => <span className="capitalize">{r.branch_type}</span> },
    { key: "debit_total", header: "Debit", align: "right", render: (r) => inr(r.debit_total) },
    { key: "credit_total", header: "Credit", align: "right", render: (r) => inr(r.credit_total) },
    { key: "closing_debit", header: "Closing Dr", align: "right", render: (r) => inr(r.closing_debit) },
    { key: "closing_credit", header: "Closing Cr", align: "right", render: (r) => inr(r.closing_credit) },
  ];

  const reconColumns = [
    { key: "source", header: "Source", cellClassName: "font-semibold text-slate-900", render: (r) => (
      <span className="inline-flex items-center gap-1.5"><AlertTriangle size={15} className="text-amber-500" />{r.source_type} {r.source_id}</span>
    ) },
    { key: "message", header: "Issue", render: (r) => r.message },
    { key: "expected_amount", header: "Expected", align: "right", render: (r) => inr(r.expected_amount) },
    { key: "actual_amount", header: "Actual", align: "right", render: (r) => inr(r.actual_amount) },
  ];

  const periodColumns = [
    { key: "period", header: "Period", render: (r) => `${fmtDate(r.period_start)} – ${fmtDate(r.period_end)}` },
    { key: "status", header: "Status", render: (r) => <StatusPill tone={r.status === "locked" ? "slate" : "emerald"}>{r.status}</StatusPill> },
    { key: "lock_reason", header: "Reason", render: (r) => r.lock_reason || "—" },
  ];

  const reconBlock = (
    <Card className="space-y-4 p-5">
      <SectionTitle
        title="Reconciliation"
        hint={`Showing ${reconciliation.length} of ${reconciliationMeta.total} open mismatches between source documents and posted journals.`}
        right={<Button variant="outline" onClick={loadAll} disabled={accountsLoading}><RefreshCw size={16} className={accountsLoading ? "animate-spin" : ""} /> Check</Button>}
      />
      {reconciliation.length === 0 ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800">
          <CheckCircle2 size={18} /> No open reconciliation issues.
        </div>
      ) : (
        <DataTable columns={reconColumns} data={reconciliation} empty="No open reconciliation issues." />
      )}
    </Card>
  );

  return (
    <Layout
      title="Accounts"
      subtitle="Campaigns feed invoices, receipts, GST, and exports"
      actions={tab === "invoices" ? <Button variant="outline" onClick={() => setInvoiceDialogOpen(true)}><Plus size={16} /> Manual invoice</Button> : null}
      mainClassName="p-3 md:p-4"
    >
      <div className="hx-accounts-page hx-accounts-compact mx-auto w-full max-w-[1400px]">
        <MetricStrip
          metrics={[
            { label: "Billed", value: inr(accountSummary?.total_billed || 0), hint: `${accountSummary?.invoice_count || 0} invoices`, tone: "blue" },
            { label: "Received", value: inr(accountSummary?.total_received || 0), hint: `${accountSummary?.payment_count || 0} receipts`, tone: "emerald" },
            { label: "Outstanding", value: inr(accountSummary?.total_outstanding || 0), hint: `${accountSummary?.overdue_invoices || 0} overdue`, tone: "amber" },
          ]}
        />

        <TabBar active={tab} onChange={setTab} tabs={tabs} />

        {/* Period filter */}
        <Card className="p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
            <Field label="From"><Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} /></Field>
            <Field label="To"><Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} /></Field>
            <Button className="w-full lg:w-auto" onClick={loadAll} disabled={accountsLoading}>
              <RefreshCw size={16} className={accountsLoading ? "animate-spin" : ""} /> Apply
            </Button>
          </div>
        </Card>

        {tab === "advanced" && (
          <TabBar active={advancedTab} onChange={setAdvancedTab} tabs={advancedTabs} />
        )}

        {tab === "workbench" && (
          <AccountWorkbench
            summary={accountSummary}
            candidates={invoiceCandidates}
            loading={accountsLoading}
            onRefresh={() => loadAll("workbench")}
            onGenerateInvoice={generateCampaignInvoice}
            onOpenTab={setTab}
          />
        )}

        {/* Invoices */}
        {tab === "invoices" && (
          <InvoicesTab columns={invoiceColumns} invoices={invoices} />
        )}

        {/* Payments */}
        {tab === "advanced" && advancedTab === "payments" && (
          <PaymentsTab columns={paymentColumns} payments={payments} onRecordPayment={() => setPaymentDialogOpen(true)} />
        )}

        {/* Payables */}
        {tab === "payables" && (
          <PayablesTab
            columns={payableColumns}
            payables={payables}
            onCreatePayable={() => setPayableDialogOpen(true)}
            onSettlePayable={() => setSettlePayableDialogOpen(true)}
          />
        )}

        {/* GST summary */}
        {tab === "gst" && summary && (
          <GSTSummaryTab summary={summary} accentBar={accentBar} />
        )}

        {/* Export templates */}
        {tab === "advanced" && advancedTab === "templates" && (
          <div className="grid grid-cols-1 gap-5 2xl:grid-cols-[minmax(0,560px)_minmax(0,1fr)]">
            <form onSubmit={saveTemplate} className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="font-display font-semibold text-slate-900">{editingTemplate ? "Edit template" : "Export template builder"}</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Template name"><Input value={templateForm.template_name} onChange={(e) => setTemplateForm({ ...templateForm, template_name: e.target.value })} /></Field>
                <Field label="Export type"><Select value={templateForm.export_type} onValueChange={(v) => setTemplateForm({ ...templateForm, export_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{availableExportTypes.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="File type"><Select value={templateForm.file_type} onValueChange={(v) => setTemplateForm({ ...templateForm, file_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="xlsx">XLSX</SelectItem><SelectItem value="csv">CSV</SelectItem></SelectContent></Select></Field>
                <Field label="CSV separator"><Input value={templateForm.column_separator} onChange={(e) => setTemplateForm({ ...templateForm, column_separator: e.target.value })} /></Field>
              </div>

              <div className="space-y-2.5">
                {templateForm.columns.map((col, index) => (
                  <div key={index} className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="inline-flex h-6 items-center rounded-md bg-blue-100 px-2 text-xs font-semibold text-blue-700">Column {index + 1}</span>
                      <Button type="button" size="sm" variant="outline" aria-label="Remove column" onClick={() => setTemplateForm({ ...templateForm, columns: templateForm.columns.filter((_, i) => i !== index) })}><Trash2 size={14} /></Button>
                    </div>
                    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                      <Field label="Header"><Input value={col.column_header} onChange={(e) => setColumn(index, { column_header: e.target.value })} /></Field>
                      <Field label="Mapped field"><Select value={col.mapped_field || "none"} onValueChange={(v) => setColumn(index, { mapped_field: v === "none" ? "" : v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Static / blank</SelectItem>{(mappedFields[templateForm.export_type] || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></Field>
                      <Field label="Static / default"><Input value={col.static_value || col.default_value || ""} onChange={(e) => setColumn(index, { static_value: e.target.value })} /></Field>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => setTemplateForm({ ...templateForm, columns: [...templateForm.columns, { column_header: "", mapped_field: "", static_value: "", default_value: "", transformation: "", required: false }] })}><Plus size={16} /> Add column</Button>
                <Button><FileSpreadsheet size={16} /> Save template</Button>
                {editingTemplate && <Button type="button" variant="outline" onClick={() => { setEditingTemplate(""); setTemplateForm(blankTemplate()); }}>Cancel edit</Button>}
              </div>
            </form>
            <DataTable columns={templateColumns} data={templates} empty="No templates yet." />
          </div>
        )}

        {/* Tally export */}
        {tab === "export" && (
          <div className="space-y-5">
            <Card className="p-5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto] lg:items-end">
                <Field label="Export type"><Select value={exportForm.export_type} onValueChange={(v) => setExportForm({ ...exportForm, export_type: v, template_id: "" })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{availableExportTypes.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="From"><Input type="date" value={exportForm.from} onChange={(e) => setExportForm({ ...exportForm, from: e.target.value })} /></Field>
                <Field label="To"><Input type="date" value={exportForm.to} onChange={(e) => setExportForm({ ...exportForm, to: e.target.value })} /></Field>
                <Field label="Template"><Select value={exportForm.template_id || "none"} onValueChange={(v) => setExportForm({ ...exportForm, template_id: v === "none" ? "" : v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Select template</SelectItem>{templatesForExport.map((t) => <SelectItem key={t.id} value={t.id}>{t.template_name}</SelectItem>)}</SelectContent></Select></Field>
                <div className="flex gap-2">
                  <Button className="flex-1 lg:flex-none" variant="outline" onClick={previewExport}>Preview</Button>
                  <Button className="flex-1 lg:flex-none" onClick={downloadExport}><Download size={16} /> Export</Button>
                </div>
              </div>
            </Card>

            <Card className="grid grid-cols-1 gap-3 p-4 text-sm sm:grid-cols-3">
              {[
                ["require_locked_period", "Require locked period"],
                ["require_clean_reconcile", "Require clean reconciliation"],
                ["require_ledger_mappings", "Require ledger mappings"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2.5 font-medium text-slate-700">
                  <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={!!exportForm[key]} onChange={(e) => setExportForm({ ...exportForm, [key]: e.target.checked })} />
                  {label}
                </label>
              ))}
            </Card>

            {preview && (
              <Card className="space-y-3 p-5">
                {preview.warnings?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{preview.warnings.join(" · ")}</div>
                )}
                <p className="text-xs text-slate-400">Preview reflects your template columns; the export file mirrors this layout exactly.</p>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        {(preview.headers || []).map((h, i) => <th key={i} className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(preview.rows || []).length === 0 ? (
                        <tr><td colSpan={(preview.headers || []).length || 1} className="px-4 py-6 text-center text-sm text-slate-400">No rows for the selected range.</td></tr>
                      ) : (
                        (preview.rows || []).map((row, idx) => (
                          <tr key={idx}>{row.map((cell, i) => <td key={i} className="whitespace-nowrap px-4 py-2.5 text-slate-700">{cell}</td>)}</tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Ledger mapping */}
        {tab === "advanced" && advancedTab === "ledgers" && (
          <Card className="space-y-1 p-5">
            <SectionTitle title="Ledger mapping" hint="Match each accounting category to the matching Tally ledger name." />
            <div className="mt-3 space-y-3">
              {ledgerDefaults.map(([key, label]) => {
                const existing = ledgerMappings.find((m) => m.category_key === key)?.ledger_name || "";
                return <LedgerRow key={key} label={label} categoryKey={key} value={existing} onSave={saveLedger} />;
              })}
            </div>
          </Card>
        )}

        {/* Trust checks */}
        {tab === "advanced" && advancedTab === "trust" && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <MetricTile label="Trial debit" value={inr(trialBalance?.total_debit || 0)} />
              <MetricTile label="Trial credit" value={inr(trialBalance?.total_credit || 0)} />
              <MetricTile label="Difference" value={inr(Math.abs(trialBalance?.difference || 0))} tone={trialBalance?.is_balanced ? "emerald" : "rose"} />
              <div className={`rounded-xl border p-4 ${trialBalance?.is_balanced ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`}>
                <p className="text-xs font-medium uppercase tracking-wide">Journal status</p>
                <p className="mt-2 flex items-center gap-2 font-display text-xl font-semibold">
                  {trialBalance?.is_balanced ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
                  {trialBalance?.is_balanced ? "Balanced" : "Mismatch"}
                </p>
              </div>
            </div>

            <Card className="space-y-3 p-5">
              <SectionTitle
                title="Trial balance"
                hint={`${trialBalance?.from || filters.from} to ${trialBalance?.to || filters.to}`}
                right={<Button variant="outline" onClick={loadAll} disabled={accountsLoading}><RefreshCw size={16} className={accountsLoading ? "animate-spin" : ""} /> Recheck</Button>}
              />
              <DataTable columns={trialColumns} data={trialBalance?.rows || []} empty="No posted journal lines in this range." getRowKey={(row) => `${row.ledger_key}-${row.branch_type}`} />
            </Card>

            <div className="grid grid-cols-1 gap-5 2xl:grid-cols-2">
              <AgingPanel title="Receivable ageing" report={receivableAging} />
              <AgingPanel title="Payable ageing" report={payableAging} />
            </div>

            <Card className="space-y-3 p-5">
              <SectionTitle title="Open reconciliation issues" hint={`Showing ${reconciliation.length} of ${reconciliationMeta.total} open issues. Resolve these before a clean accounting export.`} />
              {reconciliation.length === 0 ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800"><CheckCircle2 size={18} /> No open reconciliation issues.</div>
              ) : (
                <DataTable columns={reconColumns} data={reconciliation} empty="No open reconciliation issues." />
              )}
            </Card>
          </div>
        )}

        {/* Controls */}
        {tab === "controls" && (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
            <Card className="space-y-4 p-5">
              <SectionTitle title="Period control" hint="Lock a reviewed date range before the final Tally export." />
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Current from</p>
                  <p className="font-semibold text-slate-900">{filters.from}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Current to</p>
                  <p className="font-semibold text-slate-900">{filters.to}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setCurrentPeriodStatus("locked")}><Lock size={16} /> Lock period</Button>
                <Button variant="outline" onClick={() => setCurrentPeriodStatus("open")}><Unlock size={16} /> Reopen</Button>
              </div>
              <DataTable columns={periodColumns} data={periods} empty="No accounting periods locked yet." />
            </Card>

            {reconBlock}

            <form onSubmit={createAdjustment} className="space-y-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
              <h3 className="font-display font-semibold text-slate-900">Adjustment note</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Type"><Select value={adjustmentForm.adjustment_type} onValueChange={(v) => setAdjustmentForm({ ...adjustmentForm, adjustment_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="credit_note">Credit note</SelectItem><SelectItem value="debit_note">Debit note</SelectItem></SelectContent></Select></Field>
                <Field label="Invoice"><Select value={adjustmentForm.source_id || "none"} onValueChange={(v) => setAdjustmentForm({ ...adjustmentForm, source_id: v === "none" ? "" : v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No source</SelectItem>{invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_number}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Amount"><Input type="number" min="0" value={adjustmentForm.amount} onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })} required /></Field>
                <Field label="GST"><Input type="number" min="0" value={adjustmentForm.gst_amount} onChange={(e) => setAdjustmentForm({ ...adjustmentForm, gst_amount: e.target.value })} /></Field>
              </div>
              <Field label="Reason"><Input value={adjustmentForm.reason} onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })} /></Field>
              <Button><FileSpreadsheet size={16} /> Post adjustment</Button>
            </form>
          </div>
        )}
      </div>

      {/* Create invoice dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Create invoice</DialogTitle></DialogHeader>
          <form id="create-invoice-form" onSubmit={createInvoice} className="grid grid-cols-1 gap-x-3 gap-y-3 py-1 sm:grid-cols-2">
            <Field label="Client name" className="sm:col-span-2"><Input value={invoiceForm.client_name} onChange={(e) => setInvoiceForm({ ...invoiceForm, client_name: e.target.value })} required /></Field>
            <Field label="Invoice date"><Input type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })} /></Field>
            <Field label="Base amount"><Input type="number" min="0" value={invoiceForm.base_amount} onChange={(e) => setInvoiceForm({ ...invoiceForm, base_amount: e.target.value })} required /></Field>
            <Field label="GST mode"><Select value={invoiceForm.gst.gst_mode} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, gst: { ...invoiceForm.gst, gst_mode: v } })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="exclusive">Exclusive</SelectItem><SelectItem value="inclusive">Inclusive</SelectItem></SelectContent></Select></Field>
            <Field label="GST %"><Input type="number" value={invoiceForm.gst.gst_percentage} onChange={(e) => setInvoiceForm({ ...invoiceForm, gst: { ...invoiceForm.gst, gst_percentage: Number(e.target.value || 0) } })} /></Field>
            <Field label="Split" className="sm:col-span-2"><Select value={invoiceForm.gst.gst_treatment} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, gst: { ...invoiceForm.gst, gst_treatment: v } })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cgst_sgst">CGST + SGST</SelectItem><SelectItem value="igst">IGST</SelectItem></SelectContent></Select></Field>
            <Field label="Notes" className="sm:col-span-2"><Textarea rows={2} value={invoiceForm.notes} onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} className="resize-none" /></Field>
          </form>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>Cancel</Button>
            <Button type="submit" form="create-invoice-form"><Plus size={16} /> Create invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record payment dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
          <form id="record-payment-form" onSubmit={createPayment} className="grid grid-cols-1 gap-x-3 gap-y-3 py-1 sm:grid-cols-2">
            <Field label="Invoice" className="sm:col-span-2"><Select value={paymentForm.invoice_id || "none"} onValueChange={(v) => {
              const invoice = invoices.find((i) => i.id === v);
              setPaymentForm({ ...paymentForm, invoice_id: v === "none" ? "" : v, amount: invoice?.pending_amount ? String(invoice.pending_amount) : paymentForm.amount });
            }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No invoice</SelectItem>{invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_number} · {i.client_name} · {inr(i.pending_amount || 0)}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Amount"><Input type="number" min="0" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} required /></Field>
            <Field label="Date"><Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} /></Field>
            <Field label="Mode"><Input value={paymentForm.payment_mode} onChange={(e) => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })} /></Field>
            <Field label="Reference"><Input value={paymentForm.reference_number} onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })} /></Field>
          </form>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button type="submit" form="record-payment-form"><ReceiptIndianRupee size={16} /> Save payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create payable dialog */}
      <Dialog open={payableDialogOpen} onOpenChange={setPayableDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Create payable</DialogTitle></DialogHeader>
          <form id="create-payable-form" onSubmit={createPayable} className="grid grid-cols-1 gap-x-3 gap-y-3 py-1 sm:grid-cols-2">
            <Field label="Type" className="sm:col-span-2"><Select value={payableForm.payable_type} onValueChange={(v) => setPayableForm({ ...payableForm, payable_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{payableTypes.map((v) => <SelectItem key={v} value={v}>{v.replaceAll("_", " ")}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Vendor / landowner" className="sm:col-span-2"><Input value={payableForm.vendor_name || payableForm.landowner_name} onChange={(e) => setPayableForm({ ...payableForm, vendor_name: e.target.value, landowner_name: payableForm.payable_type === "landowner_rent" ? e.target.value : payableForm.landowner_name })} /></Field>
            <Field label="Amount"><Input type="number" min="0" value={payableForm.amount} onChange={(e) => setPayableForm({ ...payableForm, amount: e.target.value })} required /></Field>
            <Field label="Due date"><Input type="date" value={payableForm.due_date} onChange={(e) => setPayableForm({ ...payableForm, due_date: e.target.value })} /></Field>
            <Field label="GST"><Select value={payableForm.gst_applicable ? "yes" : "no"} onValueChange={(v) => setPayableForm({ ...payableForm, gst_applicable: v === "yes" })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="no">Not applicable</SelectItem><SelectItem value="yes">Applicable</SelectItem></SelectContent></Select></Field>
            <Field label="GST %"><Input type="number" value={payableForm.gst_percentage} onChange={(e) => setPayableForm({ ...payableForm, gst_percentage: e.target.value })} /></Field>
          </form>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPayableDialogOpen(false)}>Cancel</Button>
            <Button type="submit" form="create-payable-form"><Plus size={16} /> Create payable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settle payable dialog */}
      <Dialog open={settlePayableDialogOpen} onOpenChange={setSettlePayableDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Settle payable</DialogTitle></DialogHeader>
          <form id="settle-payable-form" onSubmit={createPayablePayment} className="grid grid-cols-1 gap-x-3 gap-y-3 py-1 sm:grid-cols-2">
            <Field label="Payable" className="sm:col-span-2"><Select value={payablePaymentForm.payable_id || "none"} onValueChange={(v) => {
              const payable = payables.find((p) => p.id === v);
              setPayablePaymentForm({ ...payablePaymentForm, payable_id: v === "none" ? "" : v, amount: payable?.pending_amount ? String(payable.pending_amount) : payablePaymentForm.amount });
            }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Select payable</SelectItem>{payables.filter((p) => (p.pending_amount || 0) > 0).map((p) => <SelectItem key={p.id} value={p.id}>{p.payable_type} · {p.vendor_name || p.landowner_name} · {inr(p.pending_amount)}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Amount"><Input type="number" min="0" value={payablePaymentForm.amount} onChange={(e) => setPayablePaymentForm({ ...payablePaymentForm, amount: e.target.value })} required /></Field>
            <Field label="Date"><Input type="date" value={payablePaymentForm.payment_date} onChange={(e) => setPayablePaymentForm({ ...payablePaymentForm, payment_date: e.target.value })} /></Field>
            <Field label="Mode"><Input value={payablePaymentForm.payment_mode} onChange={(e) => setPayablePaymentForm({ ...payablePaymentForm, payment_mode: e.target.value })} /></Field>
            <Field label="Reference"><Input value={payablePaymentForm.reference_number} onChange={(e) => setPayablePaymentForm({ ...payablePaymentForm, reference_number: e.target.value })} /></Field>
          </form>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSettlePayableDialogOpen(false)}>Cancel</Button>
            <Button type="submit" form="settle-payable-form"><ReceiptIndianRupee size={16} /> Save settlement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function LedgerRow({ label, categoryKey, value, onSave }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(140px,200px)_minmax(0,1fr)_auto] sm:items-center sm:gap-3">
      <div className="text-sm font-medium text-slate-700">{label}</div>
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={label} />
      <Button className="w-full sm:w-auto" variant="outline" onClick={() => onSave(categoryKey, draft)}>Save</Button>
    </div>
  );
}

function AgingPanel({ title, report }) {
  const agingColumns = [
    { key: "party_name", header: "Party", cellClassName: "font-semibold text-slate-900" },
    { key: "source_number", header: "Source" },
    { key: "branch_type", header: "Branch", render: (r) => <span className="capitalize">{r.branch_type}</span> },
    { key: "due_date", header: "Due", render: (r) => fmtDate(r.due_date) },
    { key: "days_overdue", header: "Days", align: "right" },
    { key: "pending_amount", header: "Pending", align: "right", render: (r) => inr(r.pending_amount) },
    { key: "bucket", header: "Bucket" },
  ];
  return (
    <Card className="space-y-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500">As of {report?.as_of || "—"}</p>
        </div>
        <div className="text-right">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total open</p>
          <p className="font-display text-xl font-semibold tabular-nums text-slate-900">{inr(report?.total_amount || 0)}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {["current", "1-30", "31-60", "61-90", "90+"].map((bucket) => (
          <div key={bucket} className="rounded-lg border border-slate-200 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{bucket}</p>
            <p className="mt-1 font-semibold tabular-nums text-slate-900">{inr(bucketTotal(report, bucket))}</p>
          </div>
        ))}
      </div>
      <DataTable columns={agingColumns} data={report?.rows || []} empty={`No open ${report?.kind || "items"}.`} />
    </Card>
  );
}

